import { Button, Dialog } from '@cloudflare/kumo';
import { X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { z } from 'zod';
import type { billingAccountSchema, billingOperationSchema } from '../../shared/billing.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { BillingPayment, billingPaymentQuery } from './billing-payment.tsx';

type BillingAccount = z.infer<typeof billingAccountSchema>;
type BillingOperation = z.infer<typeof billingOperationSchema>;
type ChangeKind = 'activate' | 'cancel' | 'restore';

function operationLabel(kind: BillingOperation['kind'] | ChangeKind) {
  switch (kind) {
    case 'cancel':
    case 'Cancel':
      return 'Canceling renewal…';
    case 'restore':
    case 'Restore':
      return 'Restoring renewal…';
    default:
      return 'Activating…';
  }
}

function amountLabel(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
}

export function SiteBilling({
  account,
  site,
  disabled,
  changingKind,
  onActivate,
  onRestore,
  onCancelRenewal,
}: {
  account: BillingAccount;
  site: BillingAccount['sites'][number];
  disabled: boolean;
  changingKind?: ChangeKind;
  onActivate: () => void;
  onRestore: () => void;
  onCancelRenewal: () => void;
}) {
  const [paymentOperationId, setPaymentOperationId] = useState<string>();
  const operations = account.operations.filter(
    (operation) =>
      operation.siteId === site.id &&
      ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status),
  );
  const website = operations.find((operation) => operation.service !== 'BlogAutomation');
  const blog = operations.find((operation) => operation.service === 'BlogAutomation');
  const payable = [website, blog].find((operation) => operation?.status === 'AwaitingPayment');
  const selected = operations.find((operation) => operation.id === paymentOperationId);
  const payment = useQuery({
    ...billingPaymentQuery(account.id, payable?.id, payable?.invoiceId ?? undefined),
    enabled: Boolean(payable),
  });
  const due =
    payment.data?.kind === 'Payment'
      ? ` · ${amountLabel(payment.data.amountDue, payment.data.currency)} due`
      : '';
  const date = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'long' });
  const status = website
    ? website.status === 'AwaitingPayment'
      ? `Payment required${payable?.id === website.id ? due : ''}`
      : operationLabel(website.kind)
    : changingKind
      ? operationLabel(changingKind)
      : site.cancellationAt
        ? `Will not renew · service ends ${date(site.cancellationAt)}`
        : site.prepaidUntil
          ? `Prepaid through ${date(site.prepaidUntil)}`
          : site.status === 'Inactive' || site.status === 'Pending'
            ? 'Not activated'
            : site.status === 'Trial'
              ? account.trialEndsAt
                ? `Free trial · ends ${date(account.trialEndsAt)}`
                : 'Included in the account trial'
              : 'Active · $15/month';
  const processing = changingKind ?? website?.kind ?? blog?.kind;
  return (
    <article className="flex flex-col items-start justify-between gap-4 border-b border-kumo-line py-5 min-[721px]:flex-row min-[721px]:items-center min-[721px]:gap-6">
      <div className="min-w-0">
        <h3 className="text-[16px] font-semibold break-words">
          {account.owner ? (
            <Link to="/sites/$siteId/pages" params={{ siteId: site.id }} search={{ preview: true }}>
              {site.name}
            </Link>
          ) : (
            site.name
          )}
        </h3>
        <p
          className={`mt-1.5 text-[13px] leading-[1.65] ${website?.status === 'AwaitingPayment' ? 'text-kumo-warning' : 'text-kumo-subtle'}`}
          role="status"
        >
          {status}
        </p>
        {blog ? (
          <p className="mt-1.5 text-[13px] leading-[1.65] text-kumo-subtle" role="status">
            Blog automation ·{' '}
            {blog.status === 'AwaitingPayment'
              ? `Payment required${payable?.id === blog.id ? due : ''}`
              : operationLabel(blog.kind)}
          </p>
        ) : null}
        {account.owner ? (
          <Link
            className="mt-2 inline-block text-sm text-kumo-subtle underline"
            to="/sites/$siteId/blog-automation"
            params={{ siteId: site.id }}
          >
            Blog automation
          </Link>
        ) : null}
      </div>
      {payable ? (
        <Button variant="primary" className="shrink-0" onClick={() => setPaymentOperationId(payable.id)}>
          Complete payment
        </Button>
      ) : account.owner ? (
        processing ? (
          <Button className="shrink-0" loading>
            {operationLabel(processing)}
          </Button>
        ) : ['Inactive', 'Pending'].includes(site.status) ? (
          <Button variant="primary" className="shrink-0" disabled={disabled} onClick={onActivate}>
            {!account.paymentMethod
              ? 'Add payment method to activate'
              : account.stripeSubscriptionStatus === 'Trialing'
                ? 'Include in trial'
                : account.trialEligible
                  ? 'Start free trial'
                  : 'Activate · $15/month'}
          </Button>
        ) : site.cancellationAt ? (
          <Button
            className="shrink-0"
            disabled={disabled || new Date(site.cancellationAt) <= new Date()}
            onClick={onRestore}
          >
            Restore renewal
          </Button>
        ) : (
          <Button variant="ghost" className="shrink-0" disabled={disabled} onClick={onCancelRenewal}>
            Cancel renewal
          </Button>
        )
      ) : null}
      {selected ? (
        <SiteBillingPaymentDialog
          key={selected.id}
          accountId={account.id}
          siteName={site.name}
          operation={selected}
          owner={account.owner}
          onClose={() => setPaymentOperationId(undefined)}
        />
      ) : null}
    </article>
  );
}

function SiteBillingPaymentDialog({
  accountId,
  siteName,
  operation,
  owner,
  onClose,
}: {
  accountId: string;
  siteName: string;
  operation: BillingOperation;
  owner: boolean;
  onClose: () => void;
}) {
  const [paymentPending, setPaymentPending] = useState(false);
  const update = useMutation({
    mutationFn: async (action: 'refresh' | 'retry' | 'cancellation') => {
      const path = { accountUserId: accountId, operationId: operation.id };
      if (action === 'retry')
        return unwrap(
          await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/retry', {
            params: { path },
            body: {},
          }),
        );
      if (action === 'cancellation')
        return unwrap(
          await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/cancellation', {
            params: { path },
            body: {},
          }),
        );
      return unwrap(
        await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/refresh', {
          params: { path },
          body: {},
        }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', accountId] });
    },
  });
  const payment = useQuery({
    ...billingPaymentQuery(accountId, operation.id, operation.invoiceId ?? undefined),
    enabled: operation.status === 'AwaitingPayment',
    refetchOnMount: 'always',
  });
  const busy = paymentPending || update.isPending;
  const blog = operation.service === 'BlogAutomation';
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <Dialog size="lg" className="grid gap-5 p-6 max-h-[85dvh] overflow-y-auto [&_.loading]:px-0">
        <div className="flex items-center justify-between gap-4">
          <Dialog.Title className="text-xl font-semibold">Complete payment</Dialog.Title>
          <Button
            variant="ghost"
            shape="square"
            aria-label="Close payment dialog"
            disabled={busy}
            onClick={onClose}
          >
            <X size={20} />
          </Button>
        </div>
        <Dialog.Description className="text-kumo-subtle">
          {siteName}
          {blog ? ' · Blog automation' : ''}
        </Dialog.Description>
        <ErrorNotice error={update.error} />
        {operation.error ? <p className="text-sm text-kumo-subtle">{operation.error}</p> : null}
        {operation.status === 'AwaitingPayment' ? (
          <>
            {payment.data?.kind === 'Payment' ? (
              <div className="flex items-center justify-between gap-4 border-b border-kumo-line pb-4">
                <span>Due today</span>
                <strong>{amountLabel(payment.data.amountDue, payment.data.currency)}</strong>
              </div>
            ) : null}
            <BillingPayment
              key={`${operation.id}:${operation.invoiceId ?? ''}`}
              accountId={accountId}
              operationId={operation.id}
              invoiceId={operation.invoiceId ?? undefined}
              disabled={busy || payment.isFetching}
              onPendingChange={setPaymentPending}
              onComplete={() => update.mutate('refresh')}
            />
            {owner ? (
              <div className="flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => update.mutate('retry')}>
                  Retry saved card
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => update.mutate('cancellation')}>
                  Cancel activation
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <Loading label={operationLabel(operation.kind)} />
        )}
      </Dialog>
    </Dialog.Root>
  );
}
